import type { ResourceObject, TopLevel } from './JsonApi'
import type { Unraveled } from './types/Unraveled'
import type { UnraveledResponse } from './types/UnraveledResponse'
import { deserialize } from './deserialize'

// Overload signatures — `no-redeclare` is the non-TS-aware base rule and does
// not understand TypeScript function overloads, so disable it for these lines.
// eslint-disable-next-line no-redeclare
export function unravel<T extends TopLevel>(response: T): UnraveledResponse<T>
// eslint-disable-next-line no-redeclare
export function unravel<T extends ResourceObject>(response: T): Unraveled<T>
// eslint-disable-next-line no-redeclare
// biome-ignore lint/suspicious/noExplicitAny: overload impl signature; callers use the typed overloads above.
export function unravel(response: any) {
  if (response.data === undefined) {
    // `response` is a bare resource object, not a compound document.
    return deserialize({ data: response })
  }

  // The JSON:API spec treats `data` and `included` as a single pool of
  // resources for relationship resolution, and forbids a resource from
  // appearing in both. So when a relationship points to a resource that is
  // already present in the primary `data`, the server omits it from
  // `included`. Resolving against `included` alone would leave such a
  // relationship unresolved (dropped). Build the lookup pool from both,
  // keeping `included` first so that — should a server ever send the same
  // resource in both places — the included copy wins and behaviour matches
  // the spec-compliant case. (FAME-272)
  const primaryResources = Array.isArray(response.data)
    ? response.data
    : [response.data]
  const lookupPool = [...(response.included ?? []), ...primaryResources]

  return deserialize({ data: response.data, included: lookupPool })
}
